import { type RefObject, useEffect } from 'react';
import type { RendererSnapshotCapturer } from '@renderer/index';
import { useViewerStore } from '@stores/viewerStore';
import { createLogger } from '@utils/logger';
import { buildSnapshotFilename, downloadBlob } from '@/modules/snapshot';

const log = createLogger('ExplorePage');

export function useSnapshotCapture(controllerRef: RefObject<RendererSnapshotCapturer | null>) {
  const snapshotRequestId = useViewerStore((s) => s.snapshotRequestId);

  // snapshotRequestId 是边沿触发器，支持连续截图请求。
  useEffect(() => {
    if (snapshotRequestId === 0) return;

    const ctrl = controllerRef.current;
    if (!ctrl) return;

    // 异步截图完成时，当前 renderer 会话可能已被重建。
    let cancelled = false;
    const { setSnapshotBusy } = useViewerStore.getState();

    void (async () => {
      setSnapshotBusy(true);
      try {
        const blob = await ctrl.camera.captureScreenshot({ mimeType: 'image/png' });
        if (!cancelled) {
          downloadBlob(blob, buildSnapshotFilename(useViewerStore.getState().sceneConfig));
        }
      } catch (err) {
        if (!cancelled) {
          log.error('Failed to capture snapshot', err);
        }
      } finally {
        if (!cancelled) {
          setSnapshotBusy(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [controllerRef, snapshotRequestId]);
}
